export interface FunctionParamDoc {
  name: string;
  type: string;
  description: string;
  optional?: boolean;
}

export interface FunctionExampleDoc {
  label: string;
  formula: string;
}

export interface FunctionDoc {
  id: string;
  name: string;
  tag: string;
  tagTone?: "green" | "blue" | "purple" | "orange";
  description: string;
  returns: string;
  params: FunctionParamDoc[];
  examples: FunctionExampleDoc[];
}

export interface ProductTypeDoc {
  name: string;
  description: string;
}

export const PRODUCT_TYPES = [
  { name: "NDFVanilla", description: "NDF Vanilla" },
  { name: "NDFCAP", description: "NDF com cap" },
  { name: "CallSpread", description: "Call Spread" },
  { name: "CallVanilla", description: "Call Vanilla" },
  { name: "PutVanilla", description: "Put Vanilla" },
  { name: "PutSpread", description: "Put Spread" },
  { name: "Straddle", description: "Straddle" },
  { name: "Strangle", description: "Strangle" },
  { name: "Collar", description: "Collar" },
  { name: "Fence", description: "Fence" },
] satisfies ProductTypeDoc[];

export const FUNCTION_CATALOG: FunctionDoc[] = [
  {
    id: "price-fx",
    name: "PRICE_FX",
    tag: "Precificacao",
    description: "Precifica estrutura FX usando curvas Cognito e superficie de vol B3. Retorna a taxa forward (NDF) ou o premio unitario liquido da opcao.",
    returns: "NDFVanilla / NDFCAP: taxa forward BRL/USD. Opcoes: premio unitario USD/USD.",
    params: [
      { name: "tipoInstrumento", type: "texto", description: "NDFVanilla, NDFCAP, CallSpread, CallVanilla, PutVanilla, PutSpread, Straddle, Strangle, Collar ou Fence." },
      { name: "moeda", type: "texto", description: "USD | EUR | JPY | CHF" },
      { name: "dataVencimento", type: "data", description: "Vencimento no formato yyyy-MM-dd." },
      { name: "[strike]", type: "numero", description: "Strike 1. Obrigatorio para opcoes.", optional: true },
      { name: "[strike2]", type: "numero", description: "Strike 2. CallSpread, PutSpread, Collar, Fence.", optional: true },
      { name: "[dataBase]", type: "data", description: "Data base. Padrao: D-1 util automatico.", optional: true },
      { name: "[spotManual]", type: "numero", description: "Spot manual em BRL/moeda. Se vazio, usa mercado Cognito.", optional: true },
      { name: "[carryManual]", type: "numero", description: "Taxa carry FX manual em % a.a.", optional: true },
      { name: "[preManual]", type: "numero", description: "Taxa pre BRL manual em % a.a.", optional: true },
      { name: "[volManual]", type: "numero", description: "Volatilidade manual em % a.a.", optional: true },
    ],
    examples: [
      { label: "NDF USD/BRL - taxa forward", formula: '=COGNITO.PRICE_FX("NDFVanilla";"USD";"2026-09-30")' },
      { label: "Call Spread - premio liquido", formula: '=COGNITO.PRICE_FX("CallSpread";"USD";"2026-09-30";5,2;5,5)' },
      { label: "Call Vanilla - premio", formula: '=COGNITO.PRICE_FX("CallVanilla";"USD";"2026-09-30";5,8)' },
      { label: "Collar - custo liquido", formula: '=COGNITO.PRICE_FX("Collar";"USD";"2026-09-30";5,8;6,2)' },
    ],
  },
  {
    id: "mtm-fx",
    name: "MTM_FX",
    tag: "MtM",
    tagTone: "blue",
    description: "Calcula o MtM de uma posicao FX existente. Marca a mercado usando curvas e vol do dia e retorna o valor liquido em BRL.",
    returns: "MtM liquido da estrutura em BRL.",
    params: [
      { name: "tipoInstrumento", type: "texto", description: "NDFVanilla, NDFCAP, CallSpread, CallVanilla, PutVanilla, PutSpread, Straddle, Strangle, Collar ou Fence." },
      { name: "moeda", type: "texto", description: "USD | EUR | JPY | CHF" },
      { name: "dataVencimento", type: "data", description: "Vencimento no formato yyyy-MM-dd." },
      { name: "taxaContratada", type: "numero", description: "Taxa/preco contratado da operacao." },
      { name: "[notional]", type: "numero", description: "Notional em USD", optional: true },
      { name: "[strike]", type: "numero", description: "Strike 1", optional: true },
      { name: "[strike2]", type: "numero", description: "Strike 2", optional: true },
      { name: "[dataContratacao]", type: "data", description: "Data de contratacao no formato yyyy-MM-dd.", optional: true },
      { name: "[dataBase]", type: "data", description: "Data base. Padrao: D-1 util.", optional: true },
      { name: "[spotManual]", type: "numero", description: "Spot manual em BRL/moeda. Se vazio, usa mercado Cognito.", optional: true },
      { name: "[carryManual]", type: "numero", description: "Taxa carry FX manual em % a.a.", optional: true },
      { name: "[preManual]", type: "numero", description: "Taxa pre BRL manual em % a.a.", optional: true },
      { name: "[volManual]", type: "numero", description: "Volatilidade manual em % a.a.", optional: true },
    ],
    examples: [
      { label: "NDF - MtM de posicao contratada a 5,80", formula: '=COGNITO.MTM_FX("NDFVanilla";"USD";"2026-09-30";5,8)' },
      { label: "NDF - com notional de USD 1 MM", formula: '=COGNITO.MTM_FX("NDFVanilla";"USD";"2026-09-30";5,8;1000000)' },
      { label: "Call Spread - MtM da estrutura", formula: '=COGNITO.MTM_FX("CallSpread";"USD";"2026-09-30";0,05;1000000;5,2;5,5)' },
    ],
  },
  {
    id: "impl-vol",
    name: "IMPLIED_VOL_FX",
    tag: "Vol Implicita",
    tagTone: "purple",
    description: "Calcula a volatilidade implicita de uma estrutura FX dado seu premio de mercado. Inverte o modelo para extrair a vol.",
    returns: "Volatilidade implicita % a.a. Ex: 15,00 = 15% a.a.",
    params: [
      { name: "tipoInstrumento", type: "texto", description: "CallSpread, CallVanilla, PutVanilla, PutSpread, Straddle, Strangle, Collar, Fence ou NDFCAP." },
      { name: "moeda", type: "texto", description: "USD | EUR | JPY | CHF" },
      { name: "dataVencimento", type: "data", description: "Vencimento no formato yyyy-MM-dd." },
      { name: "strike", type: "numero", description: "Strike 1" },
      { name: "[strike2]", type: "numero", description: "Strike 2", optional: true },
      { name: "[notional]", type: "numero", description: "Notional em USD", optional: true },
      { name: "[premioUnitario]", type: "numero", description: "Premio unitario observado no mercado.", optional: true },
      { name: "[premioTotal]", type: "numero", description: "Premio total observado em BRL.", optional: true },
      { name: "[taxaContratada]", type: "numero", description: "Taxa contratada em BRL/moeda. Usada em NDFCAP.", optional: true },
      { name: "[dataBase]", type: "data", description: "Data base. Padrao: D-1 util.", optional: true },
    ],
    examples: [
      { label: "Call Vanilla - vol implicita dado premio unitario", formula: '=COGNITO.IMPLIED_VOL_FX("CallVanilla";"USD";"2026-09-30";5,8;0;1000000;0,05)' },
      { label: "Call Spread - vol implicita dado premio total BRL", formula: '=COGNITO.IMPLIED_VOL_FX("CallSpread";"USD";"2026-09-30";5,2;5,5;1000000;0;50000)' },
    ],
  },
  {
    id: "loan-debt-flow",
    name: "LOAN_DEBT_FLOW",
    tag: "Divida",
    tagTone: "blue",
    description: "Monta a tabela de fluxo da divida de uma operacao de loan pelo sequential id. Use para trazer o cronograma da operacao direto para a planilha.",
    returns: "Tabela com o fluxo da divida da operacao informada.",
    params: [
      { name: "idOperacao", type: "texto", description: "Sequential id da operacao. Aceita formatos como LOAN1340 ou 1340." },
    ],
    examples: [
      { label: "Fluxo da divida pelo codigo completo", formula: '=COGNITO.LOAN_DEBT_FLOW("LOAN1340")' },
      { label: "Fluxo da divida apenas pelo numero", formula: '=COGNITO.LOAN_DEBT_FLOW("1340")' },
    ],
  },
  {
    id: "holidays",
    name: "HOLIDAYS",
    tag: "Calendario",
    tagTone: "green",
    description: "Retorna os feriados nacionais a partir de uma data inicial, com data final opcional, usando o calendario da Cognito.",
    returns: "Tabela com data e dia da semana dos feriados.",
    params: [
      { name: "dataInicial", type: "data", description: "Data inicial no formato yyyy-MM-dd." },
      { name: "[dataFinal]", type: "data", description: "Data final no formato yyyy-MM-dd. Se vazio, busca os feriados disponiveis apos a data inicial.", optional: true },
    ],
    examples: [
      { label: "Feriados em um intervalo", formula: '=COGNITO.HOLIDAYS("2026-01-01";"2026-12-31")' },
      { label: "Feriados disponiveis apos uma data", formula: '=COGNITO.HOLIDAYS("2026-01-01")' },
    ],
  },
  {
    id: "working-days",
    name: "WORKING_DAYS",
    tag: "Calendario",
    tagTone: "green",
    description: "Retorna os dias uteis em um intervalo, excluindo finais de semana e feriados nacionais.",
    returns: "Tabela com data e dia da semana dos dias uteis.",
    params: [
      { name: "dataInicial", type: "data", description: "Data inicial no formato yyyy-MM-dd." },
      { name: "dataFinal", type: "data", description: "Data final no formato yyyy-MM-dd." },
    ],
    examples: [
      { label: "Dias uteis em janeiro de 2026", formula: '=COGNITO.WORKING_DAYS("2026-01-01";"2026-01-31")' },
      { label: "Dias uteis usando formato brasileiro", formula: '=COGNITO.WORKING_DAYS("01/01/2026";"31/01/2026")' },
    ],
  },
  {
    id: "working-days-count",
    name: "WORKING_DAYS_COUNT",
    tag: "Calendario",
    tagTone: "green",
    description: "Retorna a quantidade de dias uteis em um intervalo, excluindo finais de semana e feriados nacionais.",
    returns: "Numero de dias uteis no intervalo.",
    params: [
      { name: "dataInicial", type: "data", description: "Data inicial no formato yyyy-MM-dd." },
      { name: "dataFinal", type: "data", description: "Data final no formato yyyy-MM-dd." },
    ],
    examples: [
      { label: "Quantidade de dias uteis em janeiro de 2026", formula: '=COGNITO.WORKING_DAYS_COUNT("2026-01-01";"2026-01-31")' },
      { label: "Quantidade usando formato brasileiro", formula: '=COGNITO.WORKING_DAYS_COUNT("01/01/2026";"31/01/2026")' },
    ],
  },
  {
    id: "working-days-from",
    name: "WORKING_DAYS_FROM",
    tag: "Calendario",
    tagTone: "green",
    description: "Retorna uma lista com N dias uteis a partir de uma data inicial, excluindo finais de semana e feriados nacionais.",
    returns: "Tabela com data e dia da semana dos dias uteis.",
    params: [
      { name: "dataInicial", type: "data", description: "Data inicial no formato yyyy-MM-dd." },
      { name: "quantidadeDiasUteis", type: "numero", description: "Quantidade de dias uteis a retornar." },
    ],
    examples: [
      { label: "Primeiros 10 dias uteis a partir de uma data", formula: '=COGNITO.WORKING_DAYS_FROM("2026-01-01";10)' },
      { label: "Lista usando formato brasileiro", formula: '=COGNITO.WORKING_DAYS_FROM("01/01/2026";10)' },
    ],
  },
  {
    id: "add-working-days",
    name: "ADD_WORKING_DAYS",
    tag: "Calendario",
    tagTone: "green",
    description: "Soma ou subtrai dias uteis de uma data usando o calendario Cognito, excluindo finais de semana e feriados nacionais.",
    returns: "Uma unica data final no formato DD/MM/AAAA.",
    params: [
      { name: "dataInicial", type: "data", description: "Data inicial. Aceita celula de data, DD/MM/AAAA ou yyyy-MM-dd." },
      { name: "quantidadeDiasUteis", type: "numero", description: "Quantidade inteira de dias uteis. Use valor negativo para subtrair." },
    ],
    examples: [
      { label: "Somar 455 dias uteis considerando feriados", formula: '=COGNITO.ADD_WORKING_DAYS("2026-08-03";455)' },
      { label: "Subtrair 2 dias uteis", formula: '=COGNITO.ADD_WORKING_DAYS("2028-05-03";-2)' },
      { label: "Usar diretamente na precificacao", formula: '=COGNITO.PRICE_FX("CallVanilla";"EUR";COGNITO.ADD_WORKING_DAYS("2026-08-03";455);5)' },
    ],
  },
  {
    id: "ptax-d1",
    name: "PTAX_D1",
    tag: "Mercado",
    tagTone: "orange",
    description: "Retorna a PTAX D-1 de uma moeda, considerando feriados.",
    returns: "Cotacao PTAX do campo solicitado.",
    params: [
      { name: "moeda", type: "texto", description: "Codigo da moeda. Ex: USD, EUR, JPY ou CHF." },
      { name: "[dataReferencia]", type: "data", description: "Data yyyy-MM-dd. Se vazio, usa hoje e busca D-1 util.", optional: true },
      { name: "[campo]", type: "texto", description: "venda, compra, paridadeCompra ou paridadeVenda. Padrao: venda.", optional: true },
    ],
    examples: [
      { label: "PTAX D-1 venda USD", formula: '=COGNITO.PTAX_D1("USD")' },
      { label: "PTAX D-1 compra USD por data", formula: '=COGNITO.PTAX_D1("USD";"2026-06-17";"compra")' },
    ],
  },
  {
    id: "ptax",
    name: "PTAX",
    tag: "Mercado",
    tagTone: "orange",
    description: "Retorna a PTAX de uma moeda em uma data especifica.",
    returns: "Cotacao PTAX da data e campo solicitados.",
    params: [
      { name: "moeda", type: "texto", description: "Codigo da moeda. Ex: USD, EUR, JPY ou CHF." },
      { name: "data", type: "data", description: "Data da cotacao no formato yyyy-MM-dd." },
      { name: "[campo]", type: "texto", description: "venda, compra, paridadeCompra ou paridadeVenda. Padrao: venda.", optional: true },
    ],
    examples: [
      { label: "PTAX venda USD em uma data", formula: '=COGNITO.PTAX("USD";"2026-06-16")' },
      { label: "PTAX compra USD em uma data", formula: '=COGNITO.PTAX("USD";"2026-06-16";"compra")' },
    ],
  },
  {
    id: "spot-fx",
    name: "SPOT_FX",
    tag: "Mercado",
    tagTone: "orange",
    description: "Retorna o cambio spot mais recente para uma moeda ou par.",
    returns: "Cotacao spot do campo solicitado.",
    params: [
      { name: "moedaOuPar", type: "texto", description: "Codigo da moeda ou par. Ex: USD, EUR, USD-BRL ou EUR/BRL." },
      { name: "[campo]", type: "texto", description: "bid, ask, maxima ou minima. Padrao: bid.", optional: true },
    ],
    examples: [
      { label: "Spot USD/BRL bid", formula: '=COGNITO.SPOT_FX("USD")' },
      { label: "Spot EUR/BRL ask", formula: '=COGNITO.SPOT_FX("EUR-BRL";"ask")' },
    ],
  },
  {
    id: "cdi",
    name: "CDI",
    tag: "Indicador",
    tagTone: "orange",
    description: "Retorna o CDI atual.",
    returns: "CDI atual anualizado, diario ou valor.",
    params: [
      { name: "[campo]", type: "texto", description: "anualizado, diario ou valor. Padrao: anualizado.", optional: true },
    ],
    examples: [
      { label: "CDI anualizado", formula: '=COGNITO.CDI()' },
      { label: "CDI diario", formula: '=COGNITO.CDI("diario")' },
    ],
  },
  {
    id: "ipca",
    name: "IPCA",
    tag: "Indicador",
    tagTone: "orange",
    description: "Retorna o IPCA atual.",
    returns: "IPCA mensal, acumulado no ano ou acumulado em 12 meses.",
    params: [
      { name: "[campo]", type: "texto", description: "mensal, ano ou 12m. Padrao: mensal.", optional: true },
    ],
    examples: [
      { label: "IPCA mensal", formula: '=COGNITO.IPCA()' },
      { label: "IPCA 12 meses", formula: '=COGNITO.IPCA("12m")' },
    ],
  },
  {
    id: "cdi-implicito",
    name: "CDI_IMPLICITO",
    tag: "Curva",
    tagTone: "purple",
    description: "Retorna taxa CDI/pre implicita interpolada para uma data de vencimento.",
    returns: "Taxa anual, fator anual ou fator diario da curva DI1.",
    params: [
      { name: "dataVencimento", type: "data", description: "Data alvo no formato yyyy-MM-dd." },
      { name: "[campo]", type: "texto", description: "taxaAnual, fatorAnual ou fatorDiario. Padrao: taxaAnual.", optional: true },
    ],
    examples: [
      { label: "CDI implicito para vencimento", formula: '=COGNITO.CDI_IMPLICITO("2026-09-30")' },
      { label: "Fator anual da curva DI1", formula: '=COGNITO.CDI_IMPLICITO("2026-09-30";"fatorAnual")' },
    ],
  },
  {
    id: "ipca-implicito",
    name: "IPCA_IMPLICITO",
    tag: "Curva",
    tagTone: "purple",
    description: "Retorna IPCA implicito interpolado para uma data de vencimento.",
    returns: "Taxa anual, fator anual ou fator diario da curva de IPCA implicito.",
    params: [
      { name: "dataVencimento", type: "data", description: "Data alvo no formato yyyy-MM-dd." },
      { name: "[campo]", type: "texto", description: "taxaAnual, fatorAnual ou fatorDiario. Padrao: taxaAnual.", optional: true },
    ],
    examples: [
      { label: "IPCA implicito para vencimento", formula: '=COGNITO.IPCA_IMPLICITO("2026-09-30")' },
      { label: "Fator diario da curva", formula: '=COGNITO.IPCA_IMPLICITO("2026-09-30";"fatorDiario")' },
    ],
  },
  {
    id: "forward-curve",
    name: "FORWARD_CURVE",
    tag: "Curva",
    tagTone: "purple",
    description: "Calcula forward entre duas datas para uma curva de mercado.",
    returns: "Taxa anual, taxa do periodo, fator ou taxas das pontas.",
    params: [
      { name: "curva", type: "texto", description: "CDI/DI1, IPCA/ImplicitIpca, DAP, DDI, DOL ou EUR." },
      { name: "dataInicial", type: "data", description: "Data inicial no formato yyyy-MM-dd." },
      { name: "dataFinal", type: "data", description: "Data final no formato yyyy-MM-dd." },
      { name: "[campo]", type: "texto", description: "taxaAnual, taxaPeriodo, fator, taxaInicial ou taxaFinal. Padrao: taxaAnual.", optional: true },
    ],
    examples: [
      { label: "Forward CDI entre duas datas", formula: '=COGNITO.FORWARD_CURVE("CDI";"2026-07-01";"2026-09-30")' },
      { label: "Forward IPCA do periodo", formula: '=COGNITO.FORWARD_CURVE("IPCA";"2026-07-01";"2026-09-30";"taxaPeriodo")' },
    ],
  },
  {
    id: "bmf-term",
    name: "BMF_TERM",
    tag: "Curva",
    tagTone: "purple",
    description: "Calcula termo BMF entre duas datas para uma curva.",
    returns: "Taxa do periodo, taxa anualizada ou taxas das pontas.",
    params: [
      { name: "curva", type: "texto", description: "DI/CDI, DAP, Dollar/Dolar, Euro ou ImpliedIpca/IPCA." },
      { name: "dataInicial", type: "data", description: "Data inicial no formato yyyy-MM-dd." },
      { name: "dataFinal", type: "data", description: "Data final no formato yyyy-MM-dd." },
      { name: "[campo]", type: "texto", description: "taxaPeriodo, taxaAnualizada, taxaInicial ou taxaFinal. Padrao: taxaPeriodo.", optional: true },
      { name: "[dataReferencia]", type: "data", description: "Data da curva no formato yyyy-MM-dd. Se vazio, usa ultimo dado disponivel.", optional: true },
    ],
    examples: [
      { label: "Termo BMF DI", formula: '=COGNITO.BMF_TERM("DI";"2026-07-01";"2026-09-30")' },
      { label: "Termo BMF IPCA anualizado", formula: '=COGNITO.BMF_TERM("IPCA";"2026-07-01";"2026-09-30";"taxaAnualizada")' },
    ],
  },
];
